import { useInResiStore } from "@superbright/indexeddb-orm";
import { useQueryClient } from "@tanstack/react-query";
import { lazy, useEffect, useState, type ReactNode } from "react";

import { DebugButton, useDebugFeatures } from "shared/Debug";
import { Layout } from "shared/Layout";
import { useParams } from "shared/Router";
import { EmbedThemeGate } from "theme/EmbedThemeGate";

// Lazy-loaded so its JS+CSS chunk (FullScreenToggle.module.css) is fetched
// off the critical path. The button is invisible until iframe-fullscreen
// support is detected at runtime, so a deferred mount has no UX cost.
const FullScreenToggle = lazy(() =>
  import("./ui/FullScreenToggle").then((m) => ({ default: m.FullScreenToggle }))
);

// Renders FullScreenToggle only after hydration is complete, avoiding a
// dehydrated Suspense boundary in the SSR HTML that would crash when any
// passive effect fires a synchronous render before hydration finishes.
function ClientOnlyFullScreenToggle() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) return null;
  return <FullScreenToggle />;
}

// Self-contained spinner shown while the custom theme CSS is loading. Uses only
// inline styles + an inline @keyframes so it renders correctly in the SSR HTML
// and before any external CSS (Tailwind / Chakra's runtime styles) is applied —
// it must not depend on the very stylesheets we're waiting on.
function ThemeLoader() {
  return (
    <div
      style={{
        width: "100%",
        height: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // For themed properties the SSR-baked --color-bg is already applied, so
        // the spinner matches the theme background instead of flashing white.
        backgroundColor: "var(--color-bg, #ffffff)",
      }}
    >
      <style>
        {"@keyframes inresi-theme-spin{to{transform:rotate(360deg)}}"}
      </style>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "3px solid rgba(0,0,0,0.12)",
          borderTopColor: "rgba(0,0,0,0.55)",
          animation: "inresi-theme-spin 0.7s linear infinite",
        }}
      />
    </div>
  );
}

/**
 * App-wide shell rendered by every Vike `+Page.tsx`. Gates rendering on the
 * embed theme, mounts dev tooling + the fullscreen toggle, and wraps the
 * routed page in the shared `Layout`. The page element is passed as `children`
 * (Vike owns routing, so there is no React Router `<Outlet/>`).
 */
function App({
  children,
  ssrHasCustomFonts,
}: {
  children: ReactNode;
  ssrHasCustomFonts?: boolean;
}) {
  const store = useInResiStore();
  const { propertySlug } = useParams();

  const queryClient = useQueryClient();
  const { devToolsVisible, clearStorageAndDB, clearVisitor } = useDebugFeatures(
    {
      queryClient,
    }
  );

  return (
    <EmbedThemeGate
      slug={propertySlug}
      ssrHasCustomFonts={ssrHasCustomFonts}
      fallback={<ThemeLoader />}
    >
      {devToolsVisible && (
        <DebugButton
          onClick={clearStorageAndDB}
          onClearVisitor={clearVisitor}
          store={store as unknown as Record<string, unknown>}
        />
      )}
      <ClientOnlyFullScreenToggle />
      <Layout>{children}</Layout>
    </EmbedThemeGate>
  );
}

export { App };
